import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAnalytics, logEvent, setConsent } from "firebase/analytics";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { logger, setErrorReporter } from "../utils/logger";
import { getMessaging, Messaging } from "firebase/messaging";

// Initialize Firebase
// API key is loaded from an environment variable to avoid committing it to the repo
// and to support per-environment configs. Note: in a client-side app this key ends up
// in the built JavaScript bundle and is not a secret; rely on Firebase Security Rules
// and API key restrictions for actual security.
// Local emulator mode (npm run dev:emu): opt-in only, never active by default.
// It uses the offline demo project "demo-serva", so nothing can reach production.
const useEmulators = process.env.REACT_APP_USE_EMULATORS === "true";
const EMULATOR_PROJECT_ID = "demo-serva";
const EMULATOR_HOST = "127.0.0.1";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || (useEmulators ? "demo-api-key" : undefined),
    authDomain: "godilog-82993.firebaseapp.com",
    projectId: useEmulators ? EMULATOR_PROJECT_ID : "godilog-82993",
    storageBucket: "godilog-82993.firebasestorage.app",
    appId: "1:969712695593:web:9ca4f8c475a2e061b0df38",
    measurementId: "G-3Q3LYPG783",
};

// Validate that the API key is present (the emulators accept any key)
if (!firebaseConfig.apiKey) {
    throw new Error(
        'Missing required Firebase API key (REACT_APP_FIREBASE_API_KEY).\n' +
        'Please ensure the variable is set in your .env.local file for local development ' +
        'or as a GitHub Secret for CI/CD. See .env.example for details.'
    );
}

// The app name "GodiLog" must NOT be renamed to Serva: it is part of the key under
// which Firebase Auth persists the session, so changing it would sign out every user.
const app = initializeApp(firebaseConfig, "GodiLog");

// Initialize App Check for protection against abuse. This is required for callable
// functions that enforce enforceAppCheck: true (sermon notification functions).
// In development/emulator, Firebase automatically uses debug token provider.
// In production, ensure reCAPTCHA Enterprise is configured in Firebase Console.
const recaptchaEnterpriseSiteKey = process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY?.trim();
const isProduction = !process.env.NODE_ENV || process.env.NODE_ENV === "production";
const isAppCheckEnabled = process.env.REACT_APP_ENABLE_APPCHECK !== "false";

if (useEmulators) {
    // No App Check against the emulators: the functions switch enforcement off there
    // (ENFORCE_APP_CHECK in functions/src/authz.ts).
} else if (isProduction && isAppCheckEnabled && recaptchaEnterpriseSiteKey) {
    initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaEnterpriseSiteKey),
        // Avoid background retry loops when App Check is temporarily misconfigured.
        // Tokens are still requested on demand before protected callable functions.
        isTokenAutoRefreshEnabled: false,
    });
} else if (isProduction && !isAppCheckEnabled) {
    logger.warn(
        "AppCheck",
        "Firebase App Check is disabled via REACT_APP_ENABLE_APPCHECK=false. " +
        "Protected callable functions with enforceAppCheck will fail until App Check is re-enabled."
    );
} else if (isProduction) {
    logger.warn(
        "AppCheck",
        "Firebase App Check was not initialized because REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY is missing. " +
        "Set this environment variable to enable App Check and prevent callable function failures with enforceAppCheck."
    );
}

const db = getFirestore(app);
const storageRef = getStorage(app);
const auth = getAuth(app);

// Default analytics consent to denied until user explicitly accepts (GDPR/DSGVO compliance).
// This must be set before getAnalytics() so Firebase honors it from the first event.
setConsent({ analytics_storage: "denied" });
const analytics = getAnalytics(app);
// Errors logged through utils/logger end up as Analytics "exception" events (GOD-59).
setErrorReporter((context, description, fatal) =>
    logEvent(analytics, "exception", { description: `${context}: ${description}`, fatal, context })
);

// Call this when the user grants cookie/analytics consent.
export function grantAnalyticsConsent(): void {
    setConsent({ analytics_storage: "granted" });
}

// Call this when the user explicitly denies cookie/analytics consent.
export function denyAnalyticsConsent(): void {
    setConsent({ analytics_storage: "denied" });
}

const functions = getFunctions(app, "europe-west1");

// Ports must match the "emulators" section in firebase.json.
if (useEmulators) {
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(db, EMULATOR_HOST, 8080);
    connectFunctionsEmulator(functions, EMULATOR_HOST, 5001);
}

let messaging: Messaging | null = null;
try {
    // Only available in browser contexts with secure origins and service worker support.
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
        messaging = getMessaging(app);
    }
} catch (_) {
    // Not supported in this environment (e.g. test runner, old browser)
}

export { app, db, storageRef, auth, analytics, functions, messaging };
